import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Buildings as BuildingsIcon, DoorOpen, UsersThree, WifiHigh, ArrowRight } from '@phosphor-icons/react';
import { listBuildings, listRooms } from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';

export default function Buildings() {
  const { t } = useI18n();
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Load buildings and rooms independently — a rooms failure must NOT clear the
    // buildings list (rooms only feed the per-building counts).
    listBuildings()
      .then((b) => { if (alive) setBuildings(Array.isArray(b) ? b : []); })
      .catch(() => { if (alive) setBuildings([]); })
      .finally(() => { if (alive) setLoading(false); });
    listRooms({ limit: 200, offset: 0 })
      .then((r) => { if (alive) setRooms(r?.items || []); })
      .catch(() => { if (alive) setRooms([]); });
    return () => { alive = false; };
  }, []);

  // Aggregate room/patient/device counts per building (corpus_id).
  const statsFor = (buildingId) => {
    const rs = rooms.filter((r) => r.corpus_id === buildingId);
    return {
      rooms: rs.length,
      patients: rs.reduce((s, r) => s + (r.patient_count || 0), 0),
      devices: rs.reduce((s, r) => s + (r.device_count || 0), 0),
      offline: rs.reduce((s, r) => s + (r.offline_devices || 0), 0),
    };
  };

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_buildings')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('buildings_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">{t('buildings_sub')}</p>
      </header>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : buildings.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <ul className="divide-y divide-stone_line" data-testid="buildings-list">
            {buildings.map((b) => {
              const s = statsFor(b.id);
              return (
                <li key={b.id} className="px-6 py-5 flex items-center gap-5 hover:bg-bg-alt/40 transition-colors" data-testid={`building-row-${b.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-sage-100 text-sage-600 grid place-items-center shrink-0">
                    <BuildingsIcon size={20} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-ink truncate">{b.name}</div>
                    {b.description && <div className="text-xs text-ink-muted truncate">{b.description}</div>}
                    <div className="flex items-center gap-4 mt-2 text-sm text-ink-soft flex-wrap">
                      <span className="inline-flex items-center gap-1.5"><DoorOpen size={15} weight="duotone" /> {s.rooms} {t('nav_rooms').toLowerCase()}</span>
                      <span className="inline-flex items-center gap-1.5"><UsersThree size={15} weight="duotone" /> {s.patients}</span>
                      <span className="inline-flex items-center gap-1.5"><WifiHigh size={15} weight="duotone" /> {s.devices}</span>
                      {s.offline > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-terracotta-700">
                          {s.offline} {t('status_offline').toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/rooms?building=${encodeURIComponent(b.id)}`}
                    data-testid={`building-view-rooms-${b.id}`}
                    className="inline-flex items-center gap-2 text-sm text-sage hover:text-sage-600 shrink-0"
                  >
                    {t('buildings_view_rooms')} <ArrowRight size={16} weight="bold" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
